// react and mui stuff
import { useContext, useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import Markdown from "markdown-to-jsx";

import AspectRatio from "@mui/joy/AspectRatio"
import Button from "@mui/joy/Button"
import Card from "@mui/joy/Card"
import CardContent from "@mui/joy/CardContent"
import CardOverflow from "@mui/joy/CardOverflow"
import Checkbox from "@mui/joy/Checkbox"
import FormControl from "@mui/joy/FormControl"
import FormHelperText from "@mui/joy/FormHelperText"
import FormLabel from "@mui/joy/FormLabel"
import Input from "@mui/joy/Input"
import Sheet from "@mui/joy/Sheet"
import Skeleton from "@mui/joy/Skeleton"
import Stack from "@mui/joy/Stack"
import Textarea from "@mui/joy/Textarea"
import Typography from "@mui/joy/Typography"

import { UserContext } from "../utils/contexts";
import { ToastContext } from "../components/toast";

// data and stuff
import { SponsorInfo } from "../types/sponsor";
import { sponsorAPI, imgSrc } from "../utils/backend";
import { setData } from "../utils/setdata";

// need to replace these
const ads = [
  { text: "Special Free Merch", img: imgSrc("patreon/merch.jpg") },
  { text: `Private Discord Channels`, img: imgSrc("patreon/channels.jpg"), },
  { text: "Store Discounts", img: imgSrc("patreon/discounts.jpg") }
];

const CustomLink = (props: any) => <a href={props.href} target="_blank" rel="noreferrer">{props.children}</a>;


function NotASponsor() {
  return (
    <div className="pageTitle">
      <br/>
      <Typography level="h1">Sponsor Perks</Typography>

      <br/>
      <Typography level="h3" sx={{maxWidth: "850px", margin: "auto"}}>You're not a sponsor! Support us on patreon to help us stay afloat, and get these cool benefits while you're at it!</Typography>

      {/** Sponsor Ads */}
      <br/>
      <Stack direction={{ xs: "column", sm: "row" }} spacing={2}
        useFlexGap
        sx={{ justifyContent: "center", alignItems: "center", flexWrap: 'wrap' }}
      >
        {
          ads.map(ad => (
            <Card size="lg" sx={{ whiteSpace: "pre-wrap", width: "250px" }} key={ad.img}>
              <CardOverflow><AspectRatio ratio="1/1">
                  <img src={ad.img} alt=""/>
              </AspectRatio></CardOverflow>

              <CardContent>
                <Typography level="h4">{ad.text}</Typography>
              </CardContent>
            </Card>
          ))
        }
      </Stack>

      {/** Credits */}
      <Typography level="body-xs"><Markdown options={{ overrides: { a: CustomLink } }}>The model ["A TreeJ\'s favorite hideout"](https://skfb.ly/oFIJG) is licensed under [Creative Commons Attribution](http://creativecommons.org/licenses/by/4.0/).</Markdown></Typography>
      
      {/** Sponsor Link */}
      <br/>
      <div style={{ display: "flex", justifyContent: "center", marginLeft: "10px", marginRight: "10px", marginBottom: "10px" }}>
        <Button
          sx={{ fontSize: "3em", background: "#f96854", ":hover": { background: "#d05342"}, borderRadius: "15px", paddingRight: "15px", paddingBottom: "15px" }}
          onClick={() => window.open("https://www.patreon.com/ldsgamers", "_blank")}
        >
          Support us on Patreon!
        </Button>
      </div>
    </div>
  )
}

export default function Sponsor() {
  const [sponsorInfo, setSponsorInfo] = useState<SponsorInfo>()
  const [updating, setUpdating] = useState(false)
  const [requests, setRequests] = useState<number[]>([])
  const [errors, setErrors] = useState<number[]>([])
  const [deleting, setDeleting] = useState(false)
  const [emojiName, setEmojiName] = useState<string>()
  const [emojiImg, setEmojiImg] = useState(imgSrc("placeholder/nofile.png"))
  const { user, setUser } = useContext(UserContext)
  const { setToast } = useContext(ToastContext)
  useEffect(() => {    
    // no perms?
    if (!user?.perms.isProSponsor) return;
    
    sponsorAPI.getInfo().then(info => {
      if (!setData(info, setToast, user, setUser)) return;

      // set em
      setSponsorInfo(info);
      if (info.emoji) {
        setEmojiName(info.emoji.name);
        setEmojiImg(info.emoji.url)
      }
    })
  }, [user, setUser, setToast]);

  function updateChannel(e: React.FormEvent<HTMLFormElement>) {
    // nOOPE
    e.preventDefault();

    // get data
    const formData = new FormData(e.currentTarget);
    const form = Object.fromEntries(formData.entries());

    // check for errors
    const errs = [];
    if (form.chanName.toString().length < 1 || form.chanName.toString().length > 100) errs.push(0)
    if (form.chanDesc.toString().length > 1024) errs.push(1)
    setErrors(errs)
    if (errs.length > 0) return;

    // update
    setUpdating(true);
    sponsorAPI.editChannel({ name: form.chanName.toString(), description: form.chanDesc.toString() }).then(info => {
      if (!setData(info, setToast, user, setUser, () => setUpdating(false))) return;
      
      // sets
      setSponsorInfo(info);
      setToast({ text: "Your channel was updated!", color: "success" })
      setUpdating(false);
    })
  }

  if (!user) return <div/>;
  if (user.perms.isSponsor && !user.perms.isProSponsor) return <Navigate to="/profile"/>;
  if (!user.perms.isProSponsor) return <NotASponsor/>;

  const loading = !sponsorInfo

  return (
    <div className="contentRoot">
      <h1 className="pageTitle">Pro Sponsor Settings</h1>
      <Stack direction={{ xs: "column", sm: "row" }} spacing={2} sx={{ justifyContent: "center", width: "100%" }}>

        {/** Channel Settings */}
        <Sheet sx={{ width: "100%", p: 2 }}>
          <Typography level="h3">Sponsor Channel</Typography>
          <form onSubmit={updateChannel}>
            {/** Setup Request */}
            {
              sponsorInfo && !sponsorInfo.channel && <>
                <Typography>Looks like your sponsor channel isn't set up yet! Press the button below to notify the Logistics team.</Typography>
                
                <br/>
                <br/>
                <Button fullWidth disabled={requests.includes(0)} onClick={() => {
                  // loading...
                  setUpdating(true);

                  /// getting info...
                  sponsorAPI.setup().then(info => {
                    if (!setData(info, setToast, user, setUser, () => setUpdating(false))) return;

                    // info setting...
                    setSponsorInfo(info);
                    setRequests([...requests, 0]);

                    // visual feedbacking...
                    setToast({ text: "Your request has been sent!", color: "success" });
                    setUpdating(false);
                  });
                }}>
                  Request Setup
                </Button>
              </>
            }
            {/** Loading */}
            {!sponsorInfo && <>
              <br/>
                <Typography level="body-lg"><Skeleton>Channel Name</Skeleton></Typography>
                <Skeleton variant="rectangular" height={36}/>
                <Typography><Skeleton>Must be 1-100 characters</Skeleton></Typography>
              <br/>
                <Typography level="body-lg"><Skeleton>Channel Description</Skeleton></Typography>
                <Skeleton variant="rectangular" height={60}/>
                <Typography><Skeleton>Must be less than 1000 characters</Skeleton></Typography>
              <br/>
              <Button type="submit" disabled>Save</Button>
              <Button type="reset" disabled size="sm" color="warning" variant="outlined" sx={{ marginLeft: "5px"}}>Reset</Button>
            </>
            }
            {/** Edit Channel */}
            {sponsorInfo?.channel && <>
              <br/>
                <FormControl error={errors.includes(0)} disabled={updating}>
                  <FormLabel>Channel Name</FormLabel>
                  <Skeleton variant="inline" loading={loading}>
                    <Input
                      fullWidth
                      required
                      defaultValue={sponsorInfo.channel.name}
                      name={"chanName"}
                      disabled={loading}
                    />
                  </Skeleton>
                  <FormHelperText>Must be 1-100 characters</FormHelperText>
                </FormControl>
                <br/>
                <FormControl error={errors.includes(1)} disabled={updating}>
                  <FormLabel>Channel Description</FormLabel>
                  <Skeleton variant="inline" loading={loading}>
                    <Textarea
                      disabled={loading}
                      defaultValue={sponsorInfo.channel.description}
                      minRows={2}
                      name={"chanDesc"}
                    />
                  </Skeleton>
                  <FormHelperText>Must be less than 1000 characters</FormHelperText>
                </FormControl>
                <br/>
                <Button
                  type="submit"
                  loading={updating}
                  disabled={loading}
                >
                  Save
                </Button>
                <Button type="reset" disabled={updating} size="sm" color="warning" variant="outlined" sx={{ marginLeft: "5px"}}>Reset</Button>
            </>}
          </form>
        </Sheet>

        {/** Emoji Settings */}
        <Sheet sx={{ width: "100%", p: 2}}>
          <Typography level="h3">Reaction Emoji</Typography>
            <form
            onSubmit={(e) => {
              e.preventDefault();
              const formData = new FormData(e.currentTarget);
              if (emojiName) formData.set("name", emojiName)
              formData.set("remove", deleting ? "DELETE" : "")
              
              let err = false;
              if (!emojiName || emojiName.toString().length < 2 || emojiName.toString().length > 32) err = true;
              if (err) {
                setErrors([2])
                return;
              };

              setUpdating(true);

              sponsorAPI.editEmoji(formData).then(info => {
                if (!setData(info, setToast, user, setUser, () => setUpdating(false))) return;
                setSponsorInfo(info);

                // make it so they can't click it again
                setRequests([...requests, 1])
                setToast({ text: "Your emoji change request has been submitted!", color: "success" });
                setUpdating(false);
                setEmojiImg(info.emoji?.url ?? imgSrc("placeholder/nofile.png"))
                setEmojiName(info.emoji?.name)
              });
            }}>
              {sponsorInfo && !sponsorInfo.emoji?.setup && <Typography>Looks like your ping reaction isn't set up! You can request it below.</Typography>}
              <br/>
              <FormControl error={errors.includes(1)}>
                <FormLabel>Reaction Emoji Name</FormLabel>
                <Skeleton variant="inline" loading={loading}>
                  <Input
                    autoComplete="off"
                    fullWidth
                    required
                    value={emojiName}
                    disabled={loading || deleting}
                    onChange={(e) => setEmojiName(e.currentTarget.value)}
                  />
                </Skeleton>
                <FormHelperText>Must be 2-32 characters</FormHelperText>
              </FormControl>
              <br/>
              <FormControl>
                <Checkbox
                  label="Delete Emoji Reaction"
                  disabled={updating || !sponsorInfo?.emoji?.setup}
                  checked={deleting}
                  onChange={(e) => setDeleting(e.currentTarget.checked)}
                  value={"true"}
                  />
                <FormHelperText>You will no longer recieve reactions when pinged.</FormHelperText>
              </FormControl>
              <br/>
              <FormControl>
                <FormLabel>Upload a new emoji</FormLabel>
                <Skeleton loading={loading} variant="inline">
                <div style={{ display: "flex", alignItems: "center" }}>
                  {
                    <img
                      src={emojiImg}
                      alt="emoji preview"
                      width="24px"
                      height="24px"
                    />
                  }
                  <input
                    disabled={deleting}
                    required={!sponsorInfo?.emoji?.setup}
                    type="file"
                    name="file"
                    accept="image/png, image/jpeg, image/webp, image/gif"
                    onChange={async (e) => {
                      const f = e.currentTarget.files?.item(0)
                      if (f) {
                        if (f.size >= 7 * 1024 * 1024) {
                          e.preventDefault()
                          e.currentTarget.value = "";
                          setToast({ text: "Images must be less than 7MB.", color: "warning" })
                          return;
                        }
                        const  reader = new FileReader();
                        
                        reader.onloadend = () => {
                          if (sponsorInfo?.emoji && reader.result) {
                            const name = f.name.split(".");
                            if (name.length > 0) name.pop()
                            setEmojiName(name.join("."))
                            setEmojiImg(reader.result.toString())
                          }
                        }

                        reader.readAsDataURL(f)
                      }
                    }}
                  />
                </div>
                </Skeleton>
              </FormControl>
              <br/>
              <Button type="submit" disabled={loading || updating || requests.includes(1)}>Request Change</Button>
              <Button type="reset" disabled={loading || updating} size="sm" color="warning" variant="outlined" sx={{ marginLeft: "5px"}}>Reset</Button>
            </form>
        </Sheet>
      </Stack>
    </div>
  )
}